import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Tab, Row, Col, Button, Accordion, OverlayTrigger, Tooltip, Modal, Form, Spinner } from 'react-bootstrap';
import {
    getOEChecklistQuestionsSectionsByEntity,
    editOEChecklistQuestionSectionName,
    editOEChecklistQuestion,
    createOEChecklistQuestionOrSection,
    deleteOEChecklistQuestion,
} from '@lib/api';
import { IcoInfoGrey, IcoPlus, IcoEdit, IcoCanc } from 'icone';
import { translate } from 'components/translation/translation';
import { OETrans } from 'components/translation/OETrans';
import OeApiResponseRenderer from '@components/OeApiResponseRenderer';

const initialState = {
    sectionName: '',
    entityType: '',
    questions: '',
    objective: '',
    note: '',
    code: '',
};

const CheckpointsList = ({ parentActiveKey, entities }) => {
    const [sectionsWithQuestions, setSectionsWithQuestions] = useState([]);
    const [selectedSection, setSelectedSection] = useState({ oldSection: '', newSection: '' });
    const [selectedCheckpoint, setSelectedCheckpoint] = useState(initialState);
    const [activeKey, setActiveKey] = useState('tenders');
    const [hoveredSection, setHoveredSection] = useState(null);
    const [hoveredQuestion, setHoveredQuestion] = useState(null);
    const [showModalEditSection, setShowModalEditSection] = useState(false);
    const [showModalSaveCheckpoint, setShowModalSaveCheckpoint] = useState(false);
    const [showModalDeleteCheckpoint, setShowModalDeleteCheckpoint] = useState(false);
    const [isNewSection, setIsNewSection] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [sbShow, setSbShow] = useState(<></>);
    //console.log('sectionsWithQuestions', sectionsWithQuestions);

    const fetchOEChecklistQuestionsSections = useCallback(async (entityType) => {
        const retrievedSectionsWithQuestions = await getOEChecklistQuestionsSectionsByEntity(entityType);

        if (retrievedSectionsWithQuestions.success) {
            setSectionsWithQuestions(retrievedSectionsWithQuestions.data);
        }
    }, []);

    useEffect(() => {
        if (parentActiveKey !== 'checkpoints') return;

        fetchOEChecklistQuestionsSections(activeKey);
    }, [activeKey, fetchOEChecklistQuestionsSections, parentActiveKey]);

    useEffect(() => {
        if (showModalSaveCheckpoint) {

        };
    }, [showModalSaveCheckpoint]);

    const handleShowModal = (e, setShowModal, action, sectionName, question) => {
        setSbShow(<></>);
        if (e) e.stopPropagation();

        switch (action) {
            case 'add':
                setSelectedCheckpoint({
                    sectionName: question ? sectionName : '',
                    entityType: activeKey,
                    questions: '',
                    objective: '',
                    note: '',
                    code: '',
                });

                break;
            case 'edit':
                if (question) {
                    setSelectedCheckpoint({
                        sectionName: question.sectionName,
                        entityType: question.entityType,
                        questions: question.questions,
                        objective: question.objective,
                        note: question.note,
                        code: question.code,
                    });
                } else {
                    setSelectedSection({
                        oldSection: sectionName,
                        newSection: '',
                    })
                }

                break;
            case 'delete':
                setSelectedCheckpoint({
                    sectionName: '',
                    entityType: '',
                    questions: '',
                    objective: '',
                    note: '',
                    code: question.code,
                });
                break;
            default:
                break;
        }

        setShowModal(true);
    };

    const handleCloseModal = (setShowModal) => {
        setSbShow(<></>);
        fetchOEChecklistQuestionsSections(activeKey);
        setShowModal(false);
    };

    const handleStatusBar = (dataToHandle) => {
        setSbShow(<OeApiResponseRenderer dataToHandle={dataToHandle} />);
    };

    const onChange = (e, fieldName) => {
        //console.log('e', e);
        let value;

        switch (fieldName) {
            case 'newSection':
                value = e.target.value;

                selectedSection[fieldName] = value;

                setSelectedSection({ ...selectedSection });
                break;
            case 'sectionName':
            case 'questions':
            case 'objective':
            case 'note':
                value = e.target.value;

                selectedCheckpoint[fieldName] = value;

                setSelectedCheckpoint({ ...selectedCheckpoint });
                break;
            default:
                break;
        }
    };

    const handleSave = async (e, elementType, isEdit) => {
        e.preventDefault();

        setSbShow(<></>);
        let result;

        if (elementType === 'section') {
            if (!selectedSection.newSection) {
                handleStatusBar({ success: false, message: translate('common.error.requiredFields', 'Compila i campi obbligatori') });
                return;
            }

            result = await (isEdit ? editOEChecklistQuestionSectionName(selectedSection) : createOEChecklistQuestionOrSection(selectedCheckpoint));
        } else if (elementType === 'checkpoint') {
            if (!selectedCheckpoint.questions || !selectedCheckpoint.sectionName) {
                handleStatusBar({ success: false, message: translate('common.error.requiredFields', 'Compila i campi obbligatori') });
                return;
            }

            result = await (isEdit ? editOEChecklistQuestion(selectedCheckpoint) : createOEChecklistQuestionOrSection(selectedCheckpoint));
        }

        //console.log('result', result);

        if (result.success) {
            handleStatusBar({
                success: true,
                message: isEdit
                    ? translate('common.message.edited', 'Elemento modificato con successo')
                    : translate('common.message.created', 'Elemento creato con successo'),
            });

            setTimeout(() => {
                handleCloseModal(elementType === 'section' ? setShowModalEditSection : setShowModalSaveCheckpoint);
                setIsNewSection(false);
            }, 1000);
        } else {
            handleStatusBar({
                success: false,
                message: result.message === 'Validation errors' ? Object.values(result.data).join() : translate('common.error.save', 'Si è verificato un errore durante il salvataggio delle informazioni'),
            });
        }
    };

    const handleDeleteCheckpoint = async () => {
        setSbShow(<></>);

        setIsLoading(true);

        const deleteResult = await deleteOEChecklistQuestion(selectedCheckpoint.code);

        if (deleteResult.success) {
            console.log('deleted');

            handleStatusBar({
                success: true,
                message: translate('common.message.deleted', 'Elemento eliminato correttamente'),
            });

            setTimeout(() => {
                setIsLoading(false);
                setShowModalDeleteCheckpoint(false);
                fetchOEChecklistQuestionsSections(activeKey);
            }, 2000);
        } else {
            handleStatusBar({
                success: false,
                message: deleteResult.message ?? "Si è verificato un errore durante l'eliminazione delle informazioni.",
            });

            setIsLoading(false);
        }
    };

    return (
        <>
            <Tabs className="mb-4" activeKey={activeKey} onSelect={(k) => setActiveKey(k)} variant="pills">
                {entities.map(entity => {
                    return (
                        <Tab key={entity.name} eventKey={entity.name} title={entity.label}>
                            <Row className='mx-1 mb-4'>
                                <Col className='ps-0'>
                                    <Row className="align-items-start ms-2" style={{ width: 'fit-content', backgroundColor: '#daf1f8', color: '#b0b0b0', fontWeight: '500' }}>
                                        <Col xs="auto" className="p-0 ps-1">
                                            <IcoInfoGrey className='me-1 ico-small edit' />
                                        </Col>
                                        <Col className='ps-1'>
                                            <p className='mt-2'>
                                                <OETrans code="common.checkpoints.warning.checkpointexisting" fallback={'Assicurati di aver verificato i punti di controllo già presenti in piattaforma prima di crearne nuovi.'} />
                                            </p>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>

                            <Row className="m-3">
                                <Col className='ps-0'>
                                    <Button variant="outline-info" className="me-3" onClick={() => { handleShowModal(undefined, setShowModalSaveCheckpoint, 'add', undefined, false); setIsNewSection(true) }}>
                                        <span className="d-md-inline" style={{ textTransform: 'none' }}><OETrans code="common.addsection" fallback={'Aggiungi una nuova sezione'} /></span>
                                        <IcoPlus className="second-color ico-small plus new-button ms-1" style={{ marginBottom: '2px' }} />
                                    </Button>
                                </Col>
                            </Row>

                            {sectionsWithQuestions.map((section, indexSect) => {
                                const sectionKey = section.sectionName + '-' + indexSect;
                                const isHoveredSection = hoveredSection === sectionKey;

                                return (<Accordion key={sectionKey} className='mx-3 mb-4'>
                                    <Accordion.Item eventKey={section.sectionName + '-' + (indexSect + 1)}>
                                        <Accordion.Header as="h5" className="colored-expanded-accordion" onMouseEnter={() => setHoveredSection(sectionKey)} onMouseLeave={() => setHoveredSection(null)}>
                                            <div className="row w-100 align-items-center py-1">
                                                <div className="col-auto pe-3">
                                                    <div
                                                        className="d-flex title-simple-box align-items-center"
                                                        style={{ height: '100%' }}
                                                    >
                                                        {section.sectionName}
                                                    </div>
                                                </div>
                                                <div className='col-auto ms-auto' style={{ minWidth: '35px' }}>
                                                    {isHoveredSection && (
                                                        <OverlayTrigger placement="top" overlay={<Tooltip><OETrans code="common.editsection" fallback={'Modifica nome sezione'} /></Tooltip>}>
                                                            <span className="me-3" onClick={e => handleShowModal(e, setShowModalEditSection, 'edit', section.sectionName, undefined)}>
                                                                <IcoEdit className="second-color ico-small edit" />
                                                            </span>
                                                        </OverlayTrigger>
                                                    )}
                                                </div>
                                            </div>
                                        </Accordion.Header>
                                        <Accordion.Body className="">
                                            <Row className="mb-3 ms-0">
                                                <Col className='ps-0'>
                                                    <Button variant="outline-info" className="me-3" onClick={() => handleShowModal(undefined, setShowModalSaveCheckpoint, 'add', section.sectionName, true)}>
                                                        <span className="d-md-inline" style={{ textTransform: 'none' }}><OETrans code="common.addnewcheckpoint" fallback={'Aggiungi un nuovo punto di controllo'} /></span>
                                                        <IcoPlus className="second-color ico-small plus new-button ms-1" style={{ marginBottom: '2px' }} />
                                                    </Button>
                                                </Col>
                                            </Row>

                                            {section.questions.map((question, indexQuest) => {
                                                const questionKey = question.code + '-' + indexQuest;
                                                const isHoveredQuestion = hoveredQuestion === questionKey;

                                                return (<Row key={questionKey} className='border rounded-3 my-3 mx-0 pt-1' onMouseEnter={() => setHoveredQuestion(questionKey)} onMouseLeave={() => setHoveredQuestion(null)}>
                                                    <Col className='pt-1'>
                                                        <Row className='flex-column'>
                                                            <Col><h5>{question.questions}</h5></Col>
                                                            <Col><p>{question.objective}</p></Col>
                                                        </Row>
                                                    </Col>

                                                    <Col className='col-auto ps-0' style={{ minWidth: '65px' }}>
                                                        {isHoveredQuestion && (<>
                                                            <OverlayTrigger placement="top" overlay={<Tooltip><OETrans code="common.editcheckpoint" fallback={'Modifica punto di controllo'} /></Tooltip>}>
                                                                <Button variant="link" className="me-2" onClick={e => handleShowModal(e, setShowModalSaveCheckpoint, 'edit', section.sectionName, question)}>
                                                                    <IcoEdit className="second-color ico-small edit" />
                                                                </Button>
                                                            </OverlayTrigger>
                                                            <OverlayTrigger placement="top" overlay={<Tooltip><OETrans code="common.deletecheckpoint" fallback={'Elimina punto di controllo'} /></Tooltip>}>
                                                                <span>
                                                                    <Button variant="link" className="me-0" onClick={e => handleShowModal(e, setShowModalDeleteCheckpoint, 'delete', undefined, question)} disabled={question.isUsed}>
                                                                        <IcoCanc className="second-color ico-small cancel" />
                                                                    </Button>
                                                                </span>
                                                            </OverlayTrigger>
                                                        </>
                                                        )}
                                                    </Col>
                                                </Row>)
                                            })}
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>)
                            })}
                        </Tab>
                    )
                })}
            </Tabs>

            <Modal show={showModalEditSection} onHide={() => handleCloseModal(setShowModalEditSection)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {/* concordare nome definitivo e aggiungere traduzione */}
                        Modifica sezione
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSave} id="form-save-section">
                        <Row className="mb-3 px-2">
                            <Col className="">
                                <Form.Group as={Col} className="pe-2">
                                    <Form.Label className="mb-3">
                                        {/* concordare nome definitivo e aggiungere traduzione */}
                                        Nome attuale
                                    </Form.Label>
                                    <Form.Text as="p">{selectedSection.oldSection}</Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3 px-2">
                            <Col className="">
                                <Form.Group as={Col} className="pe-2">
                                    <Form.Label className="mb-3">
                                        {/* concordare nome definitivo e aggiungere traduzione */}
                                        Nuovo nome
                                        &nbsp;<span className="required-field">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        name="newSection"
                                        value={selectedSection.newSection ?? ''}
                                        onChange={(e) => onChange(e, 'newSection')}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>

                    <Row className="mt-5 mb-2">
                        <span className="mx-2">
                            <span className="required-field">*</span>&nbsp;
                            <OETrans code="common.requiredFields" fallback={'Campi obbligatori'} />
                        </span>
                    </Row>

                    {sbShow}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={(e) => handleSave(e, 'section', !!selectedSection.oldSection)}>
                        <OETrans code="common.edit" fallback={'Modifica'} />
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal size="lg" show={showModalSaveCheckpoint} onHide={() => { handleCloseModal(setShowModalSaveCheckpoint); setIsNewSection(false) }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {/* concordare nome definitivo e aggiungere traduzione */}
                        {isNewSection ? 'Aggiungi sezione' : (selectedCheckpoint.code ? 'Modifica punto di controllo' : 'Aggiungi punto di controllo')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSave} id="form-save-checkpoint">
                        {isNewSection ? (
                            <Row className="mb-3 px-2" style={{ marginTop: '2rem' }}>
                                <Col className="">
                                    <Form.Group as={Col} className="pe-2">
                                        <Form.Label className="mb-3">
                                            {/* da aggiungere a traduzioni */}
                                            Nome sezione
                                            &nbsp;<span className="required-field">*</span>
                                        </Form.Label>
                                        <Form.Control type="text" name="sectionName" value={selectedCheckpoint.sectionName} onChange={(e) => onChange(e, 'sectionName')} />
                                    </Form.Group>
                                </Col>
                            </Row>
                        ) : null}

                        <Row className="mb-3 px-2" style={{ marginTop: '2rem' }}>
                            <Col className="">
                                <Form.Group as={Col} className="pe-2">
                                    <Form.Label className="mb-3">
                                        {/* da aggiungere a traduzioni */}
                                        Domanda
                                        &nbsp;<span className="required-field">*</span>
                                    </Form.Label>
                                    <Form.Control type="text" name="questions" value={selectedCheckpoint.questions} onChange={(e) => onChange(e, 'questions')} />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3 px-2" style={{ marginTop: '2rem' }}>
                            <Col className="">
                                <Form.Group as={Col} className="pe-2">
                                    <Form.Label className="mb-3">
                                        {/* da aggiungere a traduzioni */}
                                        Obiettivo
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={5}
                                        name="objective"
                                        value={selectedCheckpoint.objective ?? ''}
                                        onChange={(e) => onChange(e, 'objective')}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3 px-2" style={{ marginTop: '2rem' }}>
                            <Col className="">
                                <Form.Group as={Col} className="pe-2">
                                    <Form.Label className="mb-3">
                                        {/* da aggiungere a traduzioni */}
                                        Note
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={5}
                                        name="note"
                                        value={selectedCheckpoint.note ?? ''}
                                        onChange={(e) => onChange(e, 'note')}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>

                    <Row className="mt-3 mb-5">
                        <span className="mx-2">
                            <span className="required-field">*</span>&nbsp;
                            <OETrans code="common.requiredFields" fallback={'Campi obbligatori'} />
                        </span>
                    </Row>

                    {sbShow}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={(e) => handleSave(e, 'checkpoint', !!selectedCheckpoint.code)}>
                        {selectedCheckpoint.code ?
                            <OETrans code="common.edit" fallback={'Modifica'} /> :
                            <OETrans code="common.save" fallback={'Salva'} />}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showModalDeleteCheckpoint} onHide={() => handleCloseModal(setShowModalDeleteCheckpoint)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <OETrans code="common.warning" fallback={'Attenzione'} />
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mt-3 mb-5">
                        <span>
                            <OETrans code="common.warning.delete" fallback={'Sei sicuro di voler eliminare questo elemento?'} />
                        </span>
                    </div>
                    {sbShow}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="danger" onClick={() => handleDeleteCheckpoint()}>
                        <OETrans code="common.delete" fallback={'Elimina'} />
                        {isLoading && <Spinner as="span" className="ms-2" animation="border" size="sm" role="status" aria-hidden="true" />}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default CheckpointsList;
